import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { fetchWithAuth } from '../utils/fetchWithAuth'

const AuthContext = createContext({
  user: null,
  loading: true,
  refreshUser: async (_silent = false) => {},
  logout: async () => {}
})

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const refreshUser = useCallback(async (_silent = false) => {
    if (!_silent) setLoading(true)

    try {
      const res = await fetchWithAuth('/auth/me')

      if (!res.ok) {
        setUser(null)
        return
      }

      const data = await res.json()
      setUser(data)

    } catch {
      setUser(null)
    } finally {
      if (!_silent) setLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await fetchWithAuth('/auth/logout', { method: 'POST' })
    } catch {}

    setUser(null)
    setLoading(false)

    window.dispatchEvent(new Event('auth-logout'))
  }, [])

// INITIAL LOAD
useEffect(() => {
  if (window.location.pathname === '/') {
    setUser(null)
    setLoading(false)
    return
  }

  refreshUser()
}, [refreshUser])

  // HEARTBEAT (silent refresh)
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.location.pathname === '/') return
      refreshUser(true)
    }, 900_000)

    return () => clearInterval(interval)
  }, [refreshUser])

  useEffect(() => {
    const onLogout = () => {
      setUser(null)
      setLoading(false)
    }

    window.addEventListener('auth-logout', onLogout)
    return () => window.removeEventListener('auth-logout', onLogout)
  }, [])

  const value = useMemo(
    () => ({ user, loading, refreshUser, logout }),
    [user, loading, refreshUser, logout]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)